/**
 * WorkflowRenderer — React Flow 可拖拽流程引擎渲染器
 *
 * 将 ```workflow 代码块渲染为可拖拽的流程图。
 * 支持：拖拽节点、贝塞尔曲线连线、小地图、缩放。
 *
 * AI 输出 JSON 格式：
 * {
 *   "title": "案件决策树",
 *   "nodes": [
 *     {"id": "1", "label": "立案审查", "x": 250, "y": 0, "type": "input"},
 *     {"id": "2", "label": "证据收集", "x": 100, "y": 100},
 *     ...
 *   ],
 *   "edges": [
 *     {"source": "1", "target": "2", "label": "通过"},
 *     ...
 *   ]
 * }
 */

import React, { useState, useMemo, useCallback, useId } from 'react';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    BackgroundVariant,
} from 'reactflow';
import type { Node, Edge } from 'reactflow';
import 'reactflow/dist/style.css';

// Institutional Minimalist 节点样式
const NODE_STYLE = {
    background: '#FFFFFF',
    border: '1.5px solid #3B82F6',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '13px',
    fontFamily: 'Inter, "Noto Sans SC", sans-serif',
    color: '#1F2937',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const INPUT_NODE_STYLE = {
    ...NODE_STYLE,
    background: '#EFF6FF',
    border: '2px solid #3B82F6',
    fontWeight: 600 as const,
};

const OUTPUT_NODE_STYLE = {
    ...NODE_STYLE,
    background: '#F0FDF4',
    border: '2px solid #10B981',
    fontWeight: 600 as const,
};

interface WorkflowRendererProps {
    optionString: string;
}

const WorkflowRenderer: React.FC<WorkflowRendererProps> = ({ optionString }) => {
    const [error, setError] = useState<string>('');
    const uniqueId = useId().replace(/:/g, '_');

    const { initialNodes, initialEdges, title } = useMemo(() => {
        try {
            const config = JSON.parse(optionString.trim());

            // 自动布局：如果 AI 没有给坐标，我们自动垂直排列
            const nodes: Node[] = (config.nodes || []).map((n: any, i: number) => {
                const nodeType = n.type === 'input' ? 'input' : n.type === 'output' ? 'output' : 'default';
                const style = nodeType === 'input' ? INPUT_NODE_STYLE : nodeType === 'output' ? OUTPUT_NODE_STYLE : NODE_STYLE;

                return {
                    id: String(n.id),
                    type: nodeType,
                    position: { x: n.x ?? 250, y: n.y ?? i * 100 },
                    data: { label: n.label || n.id },
                    style,
                };
            });

            const edges: Edge[] = (config.edges || []).map((e: any, i: number) => ({
                id: `edge-${i}`,
                source: String(e.source),
                target: String(e.target),
                label: e.label || '',
                type: 'smoothstep',
                animated: e.animated || false,
                style: { stroke: '#9CA3AF', strokeWidth: 1.5 },
                labelStyle: { fontSize: 11, fill: '#6B7280', fontFamily: 'Inter, sans-serif' },
                labelBgStyle: { fill: '#FAFAFA', fillOpacity: 0.9 },
                labelBgPadding: [4, 6] as [number, number],
                labelBgBorderRadius: 4,
            }));

            return { initialNodes: nodes, initialEdges: edges, title: config.title || '工作流' };
        } catch (err: any) {
            setError(err.message);
            return { initialNodes: [], initialEdges: [], title: '' };
        }
    }, [optionString]);

    const [nodes, , onNodesChange] = useNodesState(initialNodes);
    const [edges, , onEdgesChange] = useEdgesState(initialEdges);

    if (error) {
        return (
            <div style={{
                margin: '12px 0', padding: '16px', background: '#F9FAFB',
                border: '1px solid #E5E7EB', borderRadius: '8px', position: 'relative',
            }}>
                <div style={{ position: 'absolute', top: '8px', right: '12px', fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>
                    WORKFLOW
                </div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151', fontFamily: 'monospace' }}>
                    {optionString}
                </pre>
            </div>
        );
    }

    return (
        <div style={{
            margin: '16px 0', border: '1px solid #E5E7EB', borderRadius: '12px',
            overflow: 'hidden', background: '#FAFAFA', position: 'relative',
        }}>
            {/* 标题栏 */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: '#F3F4F6', borderBottom: '1px solid #E5E7EB',
            }}>
                <div style={{ fontSize: '13px', color: '#1F2937', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                    {title}
                </div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F59E0B', display: 'inline-block' }} />
                    WORKFLOW · {nodes.length} 节点
                </div>
            </div>
            {/* React Flow Canvas */}
            <div id={`workflow-${uniqueId}`} style={{ width: '100%', height: '420px' }}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    fitView
                    attributionPosition="bottom-left"
                >
                    <Controls position="top-left" />
                    <MiniMap
                        style={{ background: '#F3F4F6', border: '1px solid #E5E7EB' }}
                        nodeColor="#3B82F6"
                        nodeBorderRadius={4}
                    />
                    <Background variant={BackgroundVariant.Dots} gap={16} size={0.8} color="#D1D5DB" />
                </ReactFlow>
            </div>
        </div>
    );
};

export default WorkflowRenderer;
